import { graphql, PageProps } from "gatsby"
import React, { useEffect } from "react"

import PillButton from "../components/elements/V2/Buttons/PillButton"
import TextButton from "../components/elements/V2/Buttons/TextButton"
import Section from "../components/elements/V2/Section"
import Typography from "../components/elements/V2/Typography"
import SEO from "../components/SEO"
import { Color } from "../constants/V2/color"

interface Props extends PageProps {
  header: Storyblok.Page
  pageContext: {
    uuid?: string
    pagePathContext?: string
  }
}

export const Head = ({ location }: Props): React.ReactElement => {
  return (
    <SEO
      path={location.pathname}
      meta={{
        title: "404 - Not Found",
        description: "This is a 404 page. You might be lost.",
        ogTitle: "404",
        ogDescription: "This is a 404 page. You might be lost.",
        twitterTitle: "404 - Not Found",
        twitterDescription: "This is a 404 page. You might be lost.",
      }}
    />
  )
}

const NotFoundPage = () => {
  useEffect(() => {
    const paths = Array.from(
      document.getElementsByClassName(
        "not-found-animate"
      ) as HTMLCollectionOf<SVGPathElement>
    )
    const inertia = 0.1

    const current: {
      x: number
      y: number
    }[] = []
    const target = { x: 0, y: 0 }
    const working: {
      x: number
      y: number
    }[] = []

    let firstRun = true

    const mouseMoveHandler = (e: MouseEvent) => {
      target.x = window.innerWidth - e.pageX
      target.y = window.innerHeight - e.pageY
    }

    const inertiaToTarget = (currentCoord: number, targetCoord: number) => {
      let distToGo = targetCoord - currentCoord
      let delta = currentCoord + distToGo * inertia

      if (Math.abs(distToGo) < 0.01) {
        distToGo = 0
        delta = targetCoord
      }

      return delta
    }

    const update = () => {
      if (firstRun) {
        firstRun = false
      } else {
        working.forEach((pathWorkingCoords, index) => {
          current[index] = pathWorkingCoords
        })
      }
      paths.forEach((path, index) => {
        const pathOffset = parseInt(path.getAttribute("data-offset") || "1")

        working[index].x = inertiaToTarget(
          current[index].x,
          (target.x * pathOffset) / 70
        )
        working[index].y = inertiaToTarget(
          current[index].y,
          (target.y * pathOffset) / 70
        )

        path.style.transform = `translateX(${working[index].x}px) translateY(${working[index].y}px)`
      })

      requestAnimationFrame(update)
    }

    // init functionality
    if (paths) {
      paths.forEach((path) => {
        current.push({
          x: parseFloat(getComputedStyle(path).left),
          y: parseFloat(getComputedStyle(path).top),
        })
      })
      working.push(...current)

      document.addEventListener("mousemove", mouseMoveHandler, {
        passive: true,
      })
      update()
    }

    return () => document.removeEventListener("mousemove", mouseMoveHandler)
  })

  return (
    <div className="py-3xl -mb-lg grid min-h-screen grid-cols-1 bg-charcoal sm:grid-cols-2">
      <Section className="order-2 flex flex-col justify-center gap-24 sm:order-1 md:gap-32 lg:gap-40">
        <div className="flex flex-col gap-20 md:gap-24 lg:gap-32">
          <Typography
            weight="medium"
            font="grotesk"
            size="h1"
            text="Transaction declined..."
            element="h1"
            color={Color.White}
          />
          <Typography
            weight="book"
            font="grotesk"
            size="body-lg"
            text="We can’t find the page you’re looking for, but we promise our products work far better than this page does."
            color={Color.White}
          />
        </div>
        <div className="flex flex-wrap items-baseline gap-20 md:gap-24 lg:gap-32">
          <PillButton
            size="large"
            style="bordered"
            text="Find the right product"
            linkUrl={"/za/product-finder"}
            color={Color.Blue}
            overrideTextColor={Color.White}
          />
          <TextButton
            style="text"
            text="CONTACT SALES"
            linkUrl={"/za/contact"}
            color={Color.White}
          />
        </div>
      </Section>
      <div className="relative order-1 flex h-full items-center justify-center sm:order-2">
        <svg
          viewBox="0 0 688 626"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-full w-full overflow-visible"
        >
          <path
            data-offset="1"
            d="M132.032 146.66C126.628 125.553 138.682 103.908 159.471 97.3877L416.698 16.7037C443.099 8.4226 469.951 28.1419 469.951 55.8109V240.754C469.951 263.39 451.601 281.74 428.965 281.74H198.428C179.707 281.74 163.366 269.055 158.723 250.919L132.032 146.66Z"
            fill="url(#large-gradient-block)"
            fillOpacity="0.7"
            className="not-found-animate absolute left-0 top-0"
          />
          <path
            data-offset="-2"
            d="M135.665 371.524C147.111 361.738 163.002 359.017 177.052 364.438L209.18 376.834C228.989 384.477 239.682 405.983 233.819 426.39L209.879 509.714C203.512 531.875 180.124 544.425 158.138 537.478L70.412 509.758C39.7373 500.065 31.6756 460.428 56.1271 439.524L135.665 371.524Z"
            fill="url(#medium-gradient-block)"
            fillOpacity="0.7"
            className="not-found-animate absolute left-0 top-0"
          />
          <path
            data-offset="3"
            d="M290.878 438.554C290.704 412.924 313.834 393.424 339.066 397.928L599.531 444.426C624.998 448.972 639.935 475.685 630.474 499.763L596.189 587.018C587.911 608.086 564.121 618.454 543.053 610.175L317.18 521.422C301.591 515.297 291.297 500.303 291.184 483.554L290.878 438.554Z"
            fill="url(#small-gradient-block)"
            fillOpacity="0.7"
            className="not-found-animate absolute left-0 top-0"
          />
          <path
            data-offset="-1"
            d="M235.447 326.729H208.844V362.201H179.801V326.729H99.1038V306.333L183.349 204.131H208.844V302.342H235.447V326.729ZM179.801 302.342V244.923L131.693 302.342H179.801ZM316.226 365.526C273.661 365.526 250.382 340.474 250.382 290.371V277.291C250.382 227.631 274.326 201.249 316.226 201.249C329.528 201.249 340.835 203.909 351.033 209.895C370.985 221.423 382.292 243.815 382.292 277.069V290.371C382.292 306.998 379.41 320.743 374.089 332.05C363.004 354.22 343.052 365.526 316.226 365.526ZM316.226 338.922C339.948 338.922 351.476 322.295 351.476 289.041V278.399C351.476 245.81 340.391 227.631 316.226 227.631C291.618 227.631 280.977 245.81 280.977 278.399V289.262C280.977 321.63 291.618 338.922 316.226 338.922ZM534.218 326.729H507.615V362.201H478.572V326.729H397.875V306.333L482.12 204.131H507.615V302.342H534.218V326.729ZM478.572 302.342V244.923L430.464 302.342H478.572Z"
            className="not-found-animate absolute left-0 top-0 fill-white"
          />
          <path
            data-offset="-4"
            d="M488.242 144.687C500.633 120.34 532.924 114.7 552.834 133.405L577.597 156.67C593.47 171.581 594.908 196.302 580.874 212.955L566.225 230.335C546.417 253.838 508.746 247.982 497.007 219.576L483.204 186.177C478.632 175.113 479.125 162.603 484.555 151.934L488.242 144.687Z"
            className="not-found-animate absolute left-0 top-0"
          />
          <path
            data-offset="5"
            d="M87.5646 234.542C77.5099 245.72 59.5401 244.119 51.6202 231.339L46.4967 223.071C39.596 211.936 42.6086 197.349 53.3558 189.858C68.524 179.286 89.5002 188.554 91.8986 206.886L93.1604 216.53C94.0151 223.063 91.9708 229.643 87.5646 234.542Z"
            className="not-found-animate absolute left-0 top-0"
          />
          <foreignObject x="0" y="0" width="100%" height="100%">
            <div className="h-full w-full bg-light-gray bg-opacity-50 backdrop-blur [-webkit-clip-path:url(#blur-clip)] [clip-path:url(#blur-clip)]"></div>
          </foreignObject>
          <defs>
            <clipPath id="blur-clip">
              <path
                data-offset="-4"
                d="M488.242 144.687C500.633 120.34 532.924 114.7 552.834 133.405L577.597 156.67C593.47 171.581 594.908 196.302 580.874 212.955L566.225 230.335C546.417 253.838 508.746 247.982 497.007 219.576L483.204 186.177C478.632 175.113 479.125 162.603 484.555 151.934L488.242 144.687Z"
                className="not-found-animate absolute left-0 top-0"
              />
              <path
                data-offset="5"
                d="M87.5646 234.542C77.5099 245.72 59.5401 244.119 51.6202 231.339L46.4967 223.071C39.596 211.936 42.6086 197.349 53.3558 189.858C68.524 179.286 89.5002 188.554 91.8986 206.886L93.1604 216.53C94.0151 223.063 91.9708 229.643 87.5646 234.542Z"
                className="not-found-animate absolute left-0 top-0"
              />
            </clipPath>
            <linearGradient
              id="large-gradient-block"
              x1="166.613"
              y1="-6.86888e-05"
              x2="524.73"
              y2="155.936"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#222222" />
              <stop offset="1" stopColor="#00A9E0" />
            </linearGradient>
            <linearGradient
              id="medium-gradient-block"
              x1="-35.9501"
              y1="241.552"
              x2="250.979"
              y2="368.313"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#222222" />
              <stop offset="1" stopColor="#00A9E0" />
            </linearGradient>
            <linearGradient
              id="small-gradient-block"
              x1="358.936"
              y1="339.332"
              x2="562.401"
              y2="637.972"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#222222" />
              <stop offset="1" stopColor="#00A9E0" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  )
}

export const query = graphql`
  query HeaderData {
    header: storyblokEntry(
      full_slug: { eq: "settings/navigation/top-global-navigation" }
    ) {
      content
      full_slug
    }

    headerV2: storyblokEntry(
      full_slug: { eq: "settings/navigation/default-navigation-v2" }
    ) {
      content
      full_slug
    }
  }
`

export default NotFoundPage
